import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, FilePlus2, Pencil, Plus, UserMinus } from 'lucide-react';
import {
  EMPLOYEE_STATUSES,
  EMPLOYEE_STATUS_LABELS,
  formatCents,
  type Department,
  type Employee,
  type Paginated,
  type Person,
  type Position,
} from '@amai/contracts';
import { api } from '@/lib/api';
import { errorMessage, fieldErrors } from '@/lib/errors';
import { blank, formatDate, formatDocument, formatPhone } from '@/lib/format';
import { T } from '@/design/tokens';
import { WorkspaceHeader } from '@/components/WorkspaceHeader';
import { FilterBar } from '@/components/FilterBar';
import { SmartDataTable, type Column } from '@/components/SmartDataTable';
import { ContextPanel, ReadField, StatTiles } from '@/components/ContextPanel';
import { StatusBadge, type BadgeKind } from '@/components/StatusBadge';
import { Button } from '@/components/Button';
import { Drawer, Modal } from '@/components/Modal';
import { Field, FormSection } from '@/components/FormSection';
import { MoneyInput } from '@/components/MoneyInput';
import { EmptyState, ErrorState, NoResultsState, TableSkeleton } from '@/components/states';
import { useAuth } from '@/lib/auth-context';
import { todayISO } from '@/lib/queries';
import { DateInput } from '@/components/DateInput';

const STATUS_TONE: Record<string, BadgeKind> = {
  ATIVO: 'ok',
  FERIAS: 'info',
  AFASTADO: 'warn',
  DESLIGADO: 'mute',
};

/**
 * Colaboradores.
 *
 * Nome, CPF e telefone NÃO se repetem aqui — vêm da Pessoa. Este cadastro
 * guarda o vínculo de trabalho, pela mesma razão do cadastro unificado: um
 * CPF, um cadastro. Documentos com validade (ASO, NR) viram alerta quando
 * vencem, porque colaborador com ASO vencido não pode subir na estrutura.
 */
export function ColaboradoresPage() {
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [expiredDocuments, setExpiredDocuments] = useState(false);
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Employee | null>(null);
  const [dismissing, setDismissing] = useState(false);
  const [addingDocument, setAddingDocument] = useState(false);

  const departmentsQuery = useQuery({
    queryKey: ['departments'],
    queryFn: () => api.get<{ data: Department[] }>('/departments'),
    staleTime: 5 * 60_000,
  });

  const positionsQuery = useQuery({
    queryKey: ['positions'],
    queryFn: () => api.get<{ data: Position[] }>('/positions'),
    staleTime: 5 * 60_000,
  });

  const listQuery = useQuery({
    queryKey: ['employees', { search, status, departmentId, expiredDocuments, page }],
    queryFn: () =>
      api.get<Paginated<Employee>>('/employees', {
        search: search || undefined,
        status: status || undefined,
        departmentId: departmentId || undefined,
        expiredDocuments: expiredDocuments ? 'true' : undefined,
        page,
        perPage: 25,
      }),
  });

  const detailQuery = useQuery({
    queryKey: ['employee', selectedId],
    queryFn: () => api.get<Employee>(`/employees/${selectedId}`),
    enabled: Boolean(selectedId),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['employees'] });
    void queryClient.invalidateQueries({ queryKey: ['employee'] });
    void queryClient.invalidateQueries({ queryKey: ['overview'] });
  };

  const employee = detailQuery.data;

  const columns: Array<Column<Employee>> = [
    { key: 'registration', label: 'Matrícula', mono: true, strong: true, render: (row) => row.registration },
    { key: 'name', label: 'Colaborador', render: (row) => row.name },
    { key: 'positionName', label: 'Cargo', render: (row) => blank(row.positionName) },
    { key: 'departmentName', label: 'Departamento', render: (row) => blank(row.departmentName) },
    { key: 'hireDate', label: 'Admissão', mono: true, render: (row) => formatDate(row.hireDate) },
    {
      key: 'salaryCents',
      label: 'Salário',
      right: true,
      mono: true,
      render: (row) => formatCents(row.salaryCents),
    },
    {
      key: 'status',
      label: 'Situação',
      render: (row) => (
        <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
          <StatusBadge kind={STATUS_TONE[row.status] ?? 'mute'}>
            {EMPLOYEE_STATUS_LABELS[row.status]}
          </StatusBadge>
          {row.expiredDocumentCount > 0 && (
            <StatusBadge kind="bad">{row.expiredDocumentCount} doc. vencido</StatusBadge>
          )}
        </span>
      ),
    },
  ];

  const hasFilters = Boolean(search || status || departmentId || expiredDocuments);

  return (
    <>
      <WorkspaceHeader
        title="Colaboradores"
        actions={
          can('colaboradores:criar') ? (
            <Button variant="primary" icon={Plus} onClick={() => setCreating(true)}>
              Admitir colaborador
            </Button>
          ) : undefined
        }
      />

      <div className="workspace__body">
        <FilterBar
          placeholder="Buscar por nome, matrícula ou CPF…"
          search={search}
          onSearchChange={(value) => {
            setSearch(value);
            setPage(1);
          }}
          filters={[
            {
              key: 'status',
              label: 'Situação',
              value: status,
              options: EMPLOYEE_STATUSES.map((value) => ({
                value,
                label: EMPLOYEE_STATUS_LABELS[value],
              })),
              onChange: (value) => {
                setStatus(value);
                setPage(1);
              },
            },
            {
              key: 'department',
              label: 'Departamento',
              value: departmentId,
              options: (departmentsQuery.data?.data ?? []).map((department) => ({
                value: department.id,
                label: department.name,
              })),
              onChange: (value) => {
                setDepartmentId(value);
                setPage(1);
              },
            },
          ]}
        >
          <button
            type="button"
            className={`filterbar__chip${expiredDocuments ? ' is-active' : ''}`}
            onClick={() => {
              setExpiredDocuments((v) => !v);
              setPage(1);
            }}
          >
            <AlertTriangle size={12} aria-hidden /> Documento vencido
          </button>
        </FilterBar>

        {listQuery.isPending && <TableSkeleton columns={7} />}
        {listQuery.isError && (
          <ErrorState message={errorMessage(listQuery.error)} onRetry={() => void listQuery.refetch()} />
        )}
        {listQuery.data && (
          <SmartDataTable
            caption="Colaboradores"
            columns={columns}
            rows={listQuery.data.data}
            meta={listQuery.data.meta}
            selectedId={selectedId}
            onRowClick={(row) => setSelectedId(row.id)}
            onPageChange={setPage}
            emptyState={
              hasFilters ? (
                <NoResultsState
                  onClear={() => {
                    setSearch('');
                    setStatus('');
                    setDepartmentId('');
                    setExpiredDocuments(false);
                  }}
                />
              ) : (
                <EmptyState
                  title="Nenhum colaborador"
                  description="Cadastre a pessoa primeiro, depois admita-a aqui com matrícula e cargo."
                  action={
                    can('colaboradores:criar') ? (
                      <Button variant="primary" icon={Plus} onClick={() => setCreating(true)}>
                        Admitir colaborador
                      </Button>
                    ) : undefined
                  }
                />
              )
            }
          />
        )}
      </div>

      <ContextPanel
        open={Boolean(selectedId)}
        onClose={() => setSelectedId(null)}
        title={employee ? employee.name : 'Carregando…'}
        subtitle={employee ? `matrícula ${employee.registration}` : undefined}
        tabs={['Vínculo', 'Documentos']}
        footer={
          employee ? (
            <>
              {can('colaboradores:editar') && employee.status !== 'DESLIGADO' && (
                <Button small icon={Pencil} onClick={() => setEditing(employee)}>
                  Editar
                </Button>
              )}
              {can('colaboradores:editar') && (
                <Button small icon={FilePlus2} onClick={() => setAddingDocument(true)}>
                  Anexar documento
                </Button>
              )}
              {can('colaboradores:excluir') && employee.status !== 'DESLIGADO' && (
                <Button small variant="danger" icon={UserMinus} onClick={() => setDismissing(true)}>
                  Desligar
                </Button>
              )}
            </>
          ) : undefined
        }
      >
        {(tab) => {
          if (detailQuery.isPending) return <p className="muted">Carregando…</p>;
          if (!employee) return <p className="muted">Não foi possível carregar.</p>;

          if (tab === 0) {
            return (
              <>
                <StatTiles
                  items={[
                    { label: 'Salário', value: formatCents(employee.salaryCents) },
                    { label: 'Jornada', value: `${employee.workloadHours}h` },
                  ]}
                />
                <ReadField
                  label="Situação"
                  value={
                    <StatusBadge kind={STATUS_TONE[employee.status] ?? 'mute'}>
                      {EMPLOYEE_STATUS_LABELS[employee.status]}
                    </StatusBadge>
                  }
                />
                <ReadField label="Cargo" value={blank(employee.positionName)} />
                <ReadField label="Departamento" value={blank(employee.departmentName)} />
                <ReadField label="Admissão" value={formatDate(employee.hireDate)} mono />
                {employee.dismissalDate && (
                  <>
                    <ReadField label="Desligamento" value={formatDate(employee.dismissalDate)} mono />
                    <ReadField label="Motivo" value={blank(employee.dismissalReason)} />
                  </>
                )}
                <ReadField
                  label="CPF"
                  value={employee.document ? formatDocument(employee.document) : '—'}
                  mono
                />
                <ReadField
                  label="Telefone"
                  value={employee.phone ? formatPhone(employee.phone) : '—'}
                  mono
                />
                <ReadField label="E-mail" value={blank(employee.email)} />
                {employee.notes && <ReadField label="Observações" value={employee.notes} />}
              </>
            );
          }

          if (employee.documents.length === 0) {
            return (
              <p className="muted" style={{ fontSize: 13 }}>
                Nenhum documento anexado. ASO, CTPS e certificados de NR com data
                de validade viram alerta quando vencem.
              </p>
            );
          }

          return (
            <>
              {employee.documents.map((document) => (
                <div key={document.id} className="card" style={{ padding: 12, marginBottom: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{document.label}</div>
                      <div className="mono" style={{ fontSize: 12, color: T.inkFaint, marginTop: 2 }}>
                        {document.type}
                        {document.number ? ` · ${document.number}` : ''}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      {document.expiryDate ? (
                        document.isExpired ? (
                          <StatusBadge kind="bad">
                            Vencido em {formatDate(document.expiryDate)}
                          </StatusBadge>
                        ) : (document.daysToExpiry ?? 0) <= 30 ? (
                          <StatusBadge kind="warn">
                            Vence em {document.daysToExpiry} dias
                          </StatusBadge>
                        ) : (
                          <StatusBadge kind="ok">
                            Válido até {formatDate(document.expiryDate)}
                          </StatusBadge>
                        )
                      ) : (
                        <StatusBadge kind="mute">Sem validade</StatusBadge>
                      )}
                    </div>
                  </div>
                  {document.notes && (
                    <div style={{ fontSize: 12, color: T.inkSoft, marginTop: 6 }}>{document.notes}</div>
                  )}
                </div>
              ))}
            </>
          );
        }}
      </ContextPanel>

      <EmployeeDrawer
        open={creating || Boolean(editing)}
        employee={editing}
        departments={departmentsQuery.data?.data ?? []}
        positions={positionsQuery.data?.data ?? []}
        onClose={() => {
          setCreating(false);
          setEditing(null);
        }}
        onSaved={() => {
          setCreating(false);
          setEditing(null);
          refresh();
        }}
      />

      {employee && (
        <>
          <DismissModal
            open={dismissing}
            employeeId={employee.id}
            onClose={() => setDismissing(false)}
            onDone={() => {
              setDismissing(false);
              refresh();
            }}
          />
          <DocumentModal
            open={addingDocument}
            employeeId={employee.id}
            onClose={() => setAddingDocument(false)}
            onDone={() => {
              setAddingDocument(false);
              refresh();
            }}
          />
        </>
      )}
    </>
  );
}

function EmployeeDrawer({
  open,
  employee,
  departments,
  positions,
  onClose,
  onSaved,
}: {
  open: boolean;
  employee: Employee | null;
  departments: Department[];
  positions: Position[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [personId, setPersonId] = useState('');
  const [registration, setRegistration] = useState('');
  const [positionId, setPositionId] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [hireDate, setHireDate] = useState(todayISO());
  const [salaryCents, setSalaryCents] = useState<number | null>(null);
  const [workloadHours, setWorkloadHours] = useState('44');
  const [notes, setNotes] = useState('');
  const [loadedFor, setLoadedFor] = useState<string | null>(null);

  const peopleQuery = useQuery({
    queryKey: ['people', 'colaboradores'],
    queryFn: () =>
      api.get<Paginated<Person>>('/people', { role: 'COLABORADOR', perPage: 200 }),
    enabled: open && !employee,
  });

  const formKey = open ? (employee?.id ?? 'new') : null;
  if (formKey !== loadedFor) {
    setLoadedFor(formKey);
    setPersonId(employee?.personId ?? '');
    setRegistration(employee?.registration ?? '');
    setPositionId(employee?.positionId ?? '');
    setDepartmentId(employee?.departmentId ?? '');
    setHireDate(employee?.hireDate.slice(0, 10) ?? todayISO());
    setSalaryCents(employee?.salaryCents ?? null);
    setWorkloadHours(String(employee?.workloadHours ?? 44));
    setNotes(employee?.notes ?? '');
  }

  const mutation = useMutation({
    mutationFn: (body: unknown) =>
      employee ? api.patch(`/employees/${employee.id}`, body) : api.post('/employees', body),
    onSuccess: onSaved,
  });

  const errors = fieldErrors(mutation.error);

  /** Escolher o cargo sugere o salário base e o departamento — menos digitação. */
  const applyPosition = (id: string) => {
    setPositionId(id);
    const chosen = positions.find((position) => position.id === id);
    if (!chosen) return;
    if (chosen.departmentId) setDepartmentId(chosen.departmentId);
    if (chosen.baseSalaryCents > 0 && !salaryCents) setSalaryCents(chosen.baseSalaryCents);
  };

  const canSubmit = (Boolean(employee) || Boolean(personId)) && registration.trim().length > 0;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={employee ? 'Editar vínculo' : 'Admitir colaborador'}
      description="A pessoa precisa estar cadastrada com o papel Colaborador"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            disabled={!canSubmit}
            loading={mutation.isPending}
            onClick={() =>
              mutation.mutate({
                ...(employee ? {} : { personId }),
                registration: registration.trim().toUpperCase(),
                positionId: positionId || null,
                departmentId: departmentId || null,
                hireDate,
                salaryCents: salaryCents ?? 0,
                workloadHours: Number(workloadHours) || 44,
                notes: notes.trim() || null,
              })
            }
          >
            {employee ? 'Salvar' : 'Admitir'}
          </Button>
        </>
      }
    >
      {mutation.isError && (
        <div className="login__alert" role="alert" style={{ marginBottom: 14 }}>
          {errorMessage(mutation.error)}
        </div>
      )}

      <FormSection>
        {!employee && (
          <Field label="Pessoa" htmlFor="emp-person" error={errors.personId}>
            <select
              id="emp-person"
              className="input"
              value={personId}
              onChange={(event) => setPersonId(event.target.value)}
            >
              <option value="">Selecione…</option>
              {(peopleQuery.data?.data ?? []).map((person) => (
                <option key={person.id} value={person.id}>
                  {person.name}
                  {person.document ? ` · ${formatDocument(person.document)}` : ''}
                </option>
              ))}
            </select>
          </Field>
        )}

        <div className="grid-2">
          <Field label="Matrícula" htmlFor="emp-registration" error={errors.registration}>
            <input
              id="emp-registration"
              className="input input--mono"
              value={registration}
              onChange={(event) => setRegistration(event.target.value.toUpperCase())}
              placeholder="0001"
            />
          </Field>
          <Field label="Admissão" htmlFor="emp-hire" error={errors.hireDate}>
            <DateInput
              id="emp-hire"
              className="input input--mono"
              value={hireDate}
              onChange={(iso) => setHireDate(iso)}
            />
          </Field>
        </div>

        <Field label="Cargo" htmlFor="emp-position">
          <select
            id="emp-position"
            className="input"
            value={positionId}
            onChange={(event) => applyPosition(event.target.value)}
          >
            <option value="">Sem cargo</option>
            {positions.map((position) => (
              <option key={position.id} value={position.id}>
                {position.name}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Departamento" htmlFor="emp-department">
          <select
            id="emp-department"
            className="input"
            value={departmentId}
            onChange={(event) => setDepartmentId(event.target.value)}
          >
            <option value="">Sem departamento</option>
            {departments.map((department) => (
              <option key={department.id} value={department.id}>
                {department.name}
              </option>
            ))}
          </select>
        </Field>

        <div className="grid-2">
          <Field label="Salário" htmlFor="emp-salary" error={errors.salaryCents}>
            <MoneyInput id="emp-salary" value={salaryCents} onChange={setSalaryCents} />
          </Field>
          <Field label="Jornada semanal (h)" htmlFor="emp-workload">
            <input
              id="emp-workload"
              className="input input--mono"
              inputMode="numeric"
              value={workloadHours}
              onChange={(event) => setWorkloadHours(event.target.value.replace(/\D/g, ''))}
            />
          </Field>
        </div>

        <Field label="Observações" htmlFor="emp-notes">
          <textarea
            id="emp-notes"
            className="input"
            rows={2}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
          />
        </Field>
      </FormSection>
    </Drawer>
  );
}

function DismissModal({
  open,
  employeeId,
  onClose,
  onDone,
}: {
  open: boolean;
  employeeId: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const [dismissalDate, setDismissalDate] = useState(todayISO());
  const [reason, setReason] = useState('');
  const [wasOpen, setWasOpen] = useState(open);

  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setDismissalDate(todayISO());
      setReason('');
    }
  }

  const mutation = useMutation({
    mutationFn: (body: unknown) => api.post(`/employees/${employeeId}/dismiss`, body),
    onSuccess: onDone,
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Desligar colaborador"
      description="O vínculo nunca é apagado — fica no histórico com a data e o motivo"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button
            variant="danger"
            disabled={reason.trim().length < 5}
            loading={mutation.isPending}
            onClick={() => mutation.mutate({ dismissalDate, reason: reason.trim() })}
          >
            Desligar
          </Button>
        </>
      }
    >
      {mutation.isError && (
        <div className="login__alert" role="alert" style={{ marginBottom: 14 }}>
          {errorMessage(mutation.error)}
        </div>
      )}
      <FormSection>
        <Field label="Data do desligamento" htmlFor="dis-date">
          <DateInput
            id="dis-date"
            className="input input--mono"
            value={dismissalDate}
            onChange={(iso) => setDismissalDate(iso)}
          />
        </Field>
        <Field label="Motivo" htmlFor="dis-reason" hint="Fica registrado na trilha de auditoria">
          <textarea
            id="dis-reason"
            className="input"
            rows={3}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </Field>
      </FormSection>
    </Modal>
  );
}

function DocumentModal({
  open,
  employeeId,
  onClose,
  onDone,
}: {
  open: boolean;
  employeeId: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const [type, setType] = useState('ASO');
  const [label, setLabel] = useState('');
  const [number, setNumber] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [wasOpen, setWasOpen] = useState(open);

  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setType('ASO');
      setLabel('');
      setNumber('');
      setExpiryDate('');
    }
  }

  const mutation = useMutation({
    mutationFn: (body: unknown) => api.post(`/employees/${employeeId}/documents`, body),
    onSuccess: onDone,
  });

  const errors = fieldErrors(mutation.error);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Anexar documento"
      description="Documento com validade vira alerta antes de vencer"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            disabled={type.trim().length < 2 || label.trim().length < 2}
            loading={mutation.isPending}
            onClick={() =>
              mutation.mutate({
                type: type.trim(),
                label: label.trim(),
                number: number.trim() || null,
                expiryDate: expiryDate || null,
              })
            }
          >
            Anexar
          </Button>
        </>
      }
    >
      {mutation.isError && (
        <div className="login__alert" role="alert" style={{ marginBottom: 14 }}>
          {errorMessage(mutation.error)}
        </div>
      )}
      <FormSection>
        <div className="grid-2">
          <Field label="Tipo" htmlFor="doc-type" error={errors.type}>
            <input
              id="doc-type"
              className="input"
              value={type}
              onChange={(event) => setType(event.target.value)}
              placeholder="ASO, CTPS, NR-35…"
            />
          </Field>
          <Field label="Número" htmlFor="doc-number">
            <input
              id="doc-number"
              className="input input--mono"
              value={number}
              onChange={(event) => setNumber(event.target.value)}
            />
          </Field>
        </div>
        <Field label="Descrição" htmlFor="doc-label" error={errors.label}>
          <input
            id="doc-label"
            className="input"
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            placeholder="ASO admissional"
          />
        </Field>
        <Field label="Validade" htmlFor="doc-expiry" hint="Deixe em branco se não vence">
          <DateInput
            id="doc-expiry"
            className="input input--mono"
            value={expiryDate}
            onChange={(iso) => setExpiryDate(iso)}
          />
        </Field>
      </FormSection>
    </Modal>
  );
}
